<!--
  ColorsPanel — Section 3 of the hi-fi Product editor.

  Pebble 6 of "Product editor (hi-fi redesign)"
  https://trello.com/c/Qlwx8mlT

  Responsibilities:
    - Render a swatch grid keyed on supplierMeta.colors (one tile per
      color_sku with the supplier-provided hex).
    - Owns the selection model: which color_skus the distributor will sell
      for this product. Selection state lives on the JSON:API attribute
      `selected_color_skus` (jsonb array, Track A) and is read/written via
      the productEditor Pinia store.
    - Owns the L/D override model: manual_ld (jsonb map) lets a distributor
      flip the auto-classified light/dark bucket per SKU. Two-state cycle:
      auto → manual flip → back to auto. Auto comes from
      utils/luminance.classifyLD (Track B).
    - 4 filter pills (All / Light / Dark / Selected only), a text search,
      a "Select all visible" bulk action, and a status footer summarizing
      selection counts + a smooth-scroll link to Section 4.

  Out of scope (other pebbles):
    - Routing decoration → color (count is hard-coded to 0 here; pebble 7+).
    - WCAG contrast warnings (pebble 9).
    - Color hex sourcing / supplier feed (pebble 2 + the SanMar pipeline).
-->
<template>
    <v-card data-testid="colors-card">
        <v-card-title class="colors-header">
            <v-icon icon="palette" class="colors-header-icon" />
            <div class="colors-header-text">
                <div class="d-flex align-center">
                    <div data-testid="colors-card-title" class="colors-title">
                        Colors
                    </div>
                    <HelpIcon
                        audience="owner"
                        slug="set-up-a-product"
                        heading="step-2-choose-sizes-and-colors"
                        testid="help-icon-set-up-a-product-colors"
                    />
                </div>
                <div
                    data-testid="colors-card-subtitle"
                    class="colors-subtitle"
                >
                    Pick which colors you'll sell for this product
                </div>
            </div>
        </v-card-title>

        <v-card-text>
            <div data-testid="colors-toolbar" class="colors-toolbar">
                <div class="colors-filter-pills">
                    <button
                        v-for="pill in filterPills"
                        :key="pill.key"
                        :data-testid="`colors-filter-${pill.key}`"
                        type="button"
                        class="colors-filter-pill"
                        :class="{
                            'is-active': activeFilter === pill.value,
                        }"
                        @click="activeFilter = pill.value"
                    >
                        <span class="colors-filter-pill-label">{{
                            pill.label
                        }}</span>
                        <span class="colors-filter-pill-count">{{
                            filterCounts[pill.value]
                        }}</span>
                    </button>
                </div>

                <v-text-field
                    v-model="searchQuery"
                    data-testid="colors-search"
                    label="Search"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="colors-search"
                />

                <button
                    data-testid="colors-select-all-visible"
                    type="button"
                    class="colors-select-all-visible"
                    @click="selectAllVisible"
                >
                    <v-icon icon="done_all" class="colors-select-all-icon" />
                    <span>Select all visible</span>
                </button>

                <button
                    data-testid="colors-remove-all"
                    type="button"
                    class="colors-remove-all"
                    :disabled="selectedSet.size === 0"
                    @click="removeAll"
                >
                    <v-icon icon="remove_done" class="colors-select-all-icon" />
                    <span>Remove all</span>
                </button>
            </div>

            <!--
              Loading-race cue (2026-07-09): supplierMeta is null while the
              SanMar catalog fetch is in flight. Rather than flash an empty
              grid (which reads as "this product has no colors"), show an
              explicit loading row until meta is ready. The grid stays in the
              DOM so its reactivity/tests are unaffected; this row simply
              tells the user the catalog is still arriving.
            -->
            <div
                v-if="!store.supplierMetaReady"
                data-testid="colors-loading"
                class="colors-loading"
            >
                <v-progress-circular
                    indeterminate
                    size="18"
                    width="2"
                    class="colors-loading-spinner"
                />
                <span class="colors-loading-text">Loading colors...</span>
            </div>

            <!--
              No-SKU notice (GH #3316). The shell publishes a resolved-EMPTY
              catalog for an integrated product that has no SKU yet, which
              stops the "Loading colors..." hang -- but an empty grid on its
              own says "this product has no colors", and that is a different
              fact from "you have not told me which product this is". The two
              need different actions from the user (contact the supplier vs.
              type a SKU on the next tab), so the panel names which one this
              is rather than leaving them to guess from an empty rectangle.
            -->
            <div
                v-else-if="hasNoSku"
                data-testid="colors-no-sku"
                class="colors-no-sku"
            >
                Enter a supplier SKU on the Supplier &amp; SKU tab to load this
                product's colors.
            </div>

            <!--
              Pick prompt (Jake, 2026-10-29): when Artifi can render NO color of
              this style (PC380) the editor selects no default color, so say
              why the grid opens with nothing chosen and what to do. Gone as
              soon as a color is selected. An SKU Artifi merely does not
              recognize is not this case: it still gets a default.
            -->
            <div
                v-if="showPickPrompt"
                data-testid="colors-pick-prompt"
                class="colors-pick-prompt"
                role="alert"
            >
                None of this style's colors can be previewed yet, so no color
                was selected for you. Pick the colors you want to sell.
            </div>

            <div
                id="colors-swatch-grid"
                data-testid="colors-swatch-grid"
                class="colors-swatch-grid"
            >
                <button
                    v-for="color in visibleColors"
                    :key="color.color_sku"
                    type="button"
                    :data-testid="`colors-tile-${color.color_sku}`"
                    class="colors-tile"
                    :data-artifi-unsupported="
                        isColorDimmed(color) ? 'true' : 'false'
                    "
                    :data-stranded="color.is_stranded ? 'true' : 'false'"
                    :class="{
                        'is-selected': selectedSet.has(color.color_sku),
                        'is-artifi-unsupported': isColorDimmed(color),
                        'is-stranded': color.is_stranded,
                    }"
                    :title="
                        color.is_stranded
                            ? 'This color is not in the current catalog for this product -- click to remove it'
                            : isColorDimmed(color)
                                ? 'No preview available for this color'
                                : null
                    "
                    @click="toggleColor(color.color_sku)"
                >
                    <div
                        class="color-chip"
                        :data-testid="`colors-chip-${color.color_sku}`"
                        :class="{
                            'is-placeholder':
                                !color.hex && !color.swatch_url,
                        }"
                        :style="colorChipStyle(color)"
                    />

                    <!--
                      Artifi coverage gap (SA toggle OFF): a slashed-eye badge
                      marks a color Artifi cannot preview. Shown only when the
                      toggle is OFF (when ON the color is hidden entirely) AND
                      coverage proves it unsupported (isColorDimmed encodes the
                      STRICT fail-open). Mirrors ColorFilmstrip's eye overlay.
                    -->
                    <span
                        v-if="isColorDimmed(color)"
                        :data-testid="`colors-unsupported-eye-${color.color_sku}`"
                        class="colors-tile-unsupported-eye material-icons"
                        aria-hidden="true"
                        >visibility_off</span
                    >
                    <div class="color-name">
                        {{ color.name || color.color_sku }}
                    </div>
                    <!--
                      Stranded ghost tile (GH #2153 Fix 2): the tile exists
                      ONLY so a phantom color can be removed, so it says so
                      in place of the sku code. Without a visible label the
                      user just sees an oddly-blank tile and no reason to
                      click it -- the title attribute alone is not
                      discoverable (and is unreachable on touch).
                    -->
                    <div
                        v-if="color.is_stranded"
                        :data-testid="`colors-stranded-note-${color.color_sku}`"
                        class="colors-tile-stranded-note"
                    >
                        Not in catalog - click to remove
                    </div>
                    <div v-else class="color-sku-code">
                        {{ color.color_sku }}
                    </div>

                    <span
                        v-if="selectedSet.has(color.color_sku)"
                        :data-testid="`colors-check-pip-${color.color_sku}`"
                        class="check-pip"
                    >
                        <v-icon icon="check" />
                    </span>

                    <!--
                      Unrenderable-colors footnote dot (blueprint 5.2, Phase
                      5): a small badge distinct from the check pip / L-D pip,
                      shown whenever coverageFootnote() is non-null for this
                      color -- i.e. this color is selectable AND selected, but
                      some or all of its Artifi preview views won't render.
                      Does NOT replace coverageFilteredColors (that filter
                      governs "can this color be selected at all"; this dot
                      governs "selected, but a preview gap exists"). Detail is
                      named in the footnote list below the grid, not here.
                    -->
                    <span
                        v-if="coverageFootnote(color.name ?? color.color_sku)"
                        :data-testid="`colors-tile-footnote-${color.color_sku}`"
                        class="colors-tile-footnote-dot"
                        title="No preview available for some or all views of this color"
                    />

                    <!--
                      L/D pip — replaced cycle-on-click with explicit popover
                      picker (task #168/#174). The LDPipPopover component owns
                      the open/close state and emits update:manual-ld with the
                      chosen value ('light' | 'dark' | null for Auto).
                      setLDOverride() maps that into the manual_ld store field.

                      Data attributes are preserved on the host span for test
                      selectors and external tooling — they are forwarded
                      from LDPipPopover via data-testid / data-ld-state /
                      data-ld-confidence so existing suite queries still work.
                    -->
                    <LDPipPopover
                        :data-testid="`colors-ld-pip-${color.color_sku}`"
                        :data-ld-state="ldPipState(color)"
                        :data-ld-confidence="ldPipConfidence(color)"
                        :color="color"
                        :manual-override="manualLd[color.color_sku] ?? null"
                        @update:manual-ld="(val) => setLDOverride(color.color_sku, val)"
                    />
                </button>
            </div>

            <!--
              Hidden-colors disclosure (Jake 2026-10-27): the hide-unpreviewable
              feature withholds colors the editor cannot draw; this keeps them
              reachable instead of making them disappear. Collapsed by
              default. The reason is the sentence itself; a revealed tile
              carries the dim treatment + 'No preview available' title.
            -->
            <div
                v-if="showHiddenDisclosure"
                data-testid="colors-hidden-disclosure"
                class="colors-hidden-disclosure"
            >
                <span
                    class="colors-hidden-disclosure-text"
                    aria-live="polite"
                    data-testid="colors-hidden-disclosure-text"
                >{{ hiddenDisclosureText }}</span>
                <button
                    type="button"
                    data-testid="colors-hidden-toggle"
                    class="colors-hidden-toggle"
                    aria-controls="colors-swatch-grid"
                    :aria-expanded="revealHidden ? 'true' : 'false'"
                    @click="revealHidden = !revealHidden"
                >
                    {{ revealHidden ? 'Hide' : 'Show' }}
                </button>
            </div>

            <div
                data-testid="colors-status-footer"
                class="colors-status-footer"
            >
                <span class="colors-status-text">
                    {{ statusFooterCounts.selected }} selected ·
                    {{ statusFooterCounts.light }} light ·
                    {{ statusFooterCounts.dark }} dark · routed across
                    {{ statusFooterCounts.decorations }} decorations
                </span>
                <a
                    data-testid="colors-decoration-link"
                    href="#"
                    class="colors-decoration-link"
                    @click.prevent="scrollToDecorations"
                >
                    See routing in Decorations ↓
                </a>
            </div>

            <!--
              Footnote list (blueprint 5.2, Phase 5): one row per SELECTED
              color that has a coverageFootnote (selected colors only --
              an unselected color with a gap has nothing to "remove"). Text
              format matches the blueprint exactly:
                allFail   -> "{name} -- no preview available for this color"
                partial   -> "{name} -- no preview on {views}"
              Remove reuses the EXISTING toggleColor(sku) deselect path
              (D4 soft-remove, section 6) -- no new backend action.
            -->
            <div
                v-if="footnoteEntries.length > 0"
                data-testid="colors-footnote-list"
                class="colors-footnote-list"
            >
                <div
                    v-for="entry in footnoteEntries"
                    :key="entry.color.color_sku"
                    :data-testid="`colors-footnote-${entry.color.color_sku}`"
                    class="colors-footnote-row"
                >
                    <span class="colors-footnote-text">{{ entry.text }}</span>
                    <button
                        type="button"
                        :data-testid="`colors-footnote-remove-${entry.color.color_sku}`"
                        class="colors-footnote-remove"
                        @click="toggleColor(entry.color.color_sku)"
                    >
                        Remove
                    </button>
                </div>
            </div>
        </v-card-text>
        <!--
          Undo for a deselect that removed storefront images (#4071 gate,
          2026-10-06). Pruning is Jake's rule; this only guards an accidental
          click, so it is a ~10s snackbar, never a confirm dialog.
        -->
        <v-snackbar
            v-model="pruneUndo.open"
            :timeout="PRUNE_UNDO_TIMEOUT_MS"
            data-testid="colors-prune-undo-snackbar"
        >
            {{ pruneUndo.message }}
            <template #actions>
                <v-btn
                    variant="text"
                    data-testid="colors-prune-undo"
                    @click="undoPrune"
                >
                    Undo
                </v-btn>
            </template>
        </v-snackbar>
    </v-card>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useProductEditorStore } from '@/stores/productEditor'
import { useProductEditorUiState } from '@/stores/productEditorUiState'
import { classifyLD, effectiveLD as effectiveLDUtil } from '@/utils/luminance'
import { classifyByName } from '@/utils/colorNameHeuristic'
import { useArtifiCoverage } from '@/composables/useArtifiCoverage'
import { useHideUnpreviewableColors } from '@/composables/useHideUnpreviewableColors'
import { artifiPreviewEligible } from '@/utils/supplier-entitlement'
import { colorNamesForSkus, restoreColors, withoutColors } from '@/utils/storefrontImages'
import { normalizeColorNameForMatch } from '@/utils/colorNameMatch'
import axiosInstance from '@/axios'
import LDPipPopover from '@/pages/distributor/store-editor/StoreProducts/Editor/sections/LDPipPopover.vue'
import HelpIcon from '@/components/Help/HelpIcon.vue'

const store = useProductEditorStore()

// Artifi coverage for this style, keyed on the product's supplier SKU --
// the SAME composable (and therefore the SAME normalized predicate) the
// ColorFilmstrip eye uses. Change #3: colors Artifi definitively cannot
// decorate are HIDDEN from the picker so a distributor cannot select them.
const productItemNumber = computed(
    () => store.serverState?.data?.attributes?.product_item_number ?? null,
)
// Supplier gate (2026-07-10, Jake -- P0 regression fix). The Colors TAB
// mounts this panel, which called useArtifiCoverage(productItemNumber)
// UNGATED -> a NAMED non-SanMar product fired
// GET /api/artifi/supported_color_variants and got a 502. Feed the SKU only
// for a SanMar-eligible supplier (SanMar-named OR unknown/unset); null for a
// named non-SanMar supplier. useArtifiCoverage's watcher skips the fetch on
// a null SKU, so a suppressed product never hits the endpoint (and its
// coverage set stays empty -> the hide filter below fails open, showing
// every color). This REPLACES the old is_smart_product gate, which wrongly
// skipped real SanMar products whose flag is false/null (the P0 this
// repairs). getEffectiveAttribute honors in-flight edits, matching the idiom
// in ColorFilmstrip.vue / StorefrontListingPanel.vue. See
// @/utils/isSanmarSupplier.
// Keyed on the resolved supplier_key since 2026-10-07 (S&S previews): see
// artifiPreviewEligible in @/utils/supplier-entitlement.
const isArtifiEligible = computed(() =>
    artifiPreviewEligible({
        supplierKey: store.supplierKey,
        supplierKeyResolved: store.supplierKeyResolved,
        supplierName: store.getEffectiveAttribute?.('supplier_name'),
    }),
)
const artifiCoverageSku = computed(
    () => (isArtifiEligible.value ? productItemNumber.value : null),
)
const {
    supportedSet: artifiSupportedSet,
    loading: artifiLoading,
    error: artifiError,
    notFound: artifiNotFound,
    styleUnsupported: artifiStyleUnsupported,
    isUnsupported: isArtifiUnsupported,
    coverageFootnote,
} = useArtifiCoverage(artifiCoverageSku)

// SuperAdmin toggle (hide_unpreviewable_color_variants): GATES the coverage
// hiding below. ON (the launch default) -> hide unpreviewable colors; OFF ->
// SHOW them, dimmed/flagged (data-artifi-unsupported + is-artifi-unsupported)
// instead of removed. Shared read (one GET per page load, deduped) so this
// panel and SupplierSkuPanel always agree. Fails SAFE to true (hide) while
// loading / on error -- an uncertain read never surprise-reveals colors.
const { hideUnpreviewable } = useHideUnpreviewableColors()

// Component-local filter state — by spec, filters do NOT live on the store.
// activeFilter is one of 'all' | 'light' | 'dark' | 'selected'.
const activeFilter = ref('all')
// No-vanish state for hidden (unrenderable) colors; see coverageFilteredColors.
const revealHidden = ref(false)
const releasedSkus = ref(new Set())
const searchQuery = ref('')

const filterPills = [
    { key: 'all', value: 'all', label: 'All' },
    { key: 'light', value: 'light', label: 'Light' },
    { key: 'dark', value: 'dark', label: 'Dark' },
    {
        key: 'selected-only',
        value: 'selected',
        label: 'Selected only',
    },
]

// ---- canonical reads from store ----
//
// All attribute reads go through store.getEffectiveAttribute so the panel
// reflects in-flight (dirty) edits immediately — not just after save. Pre-fix
// (2026-05-18 user report: "I can't select colors"), these computeds read
// serverState directly, so:
//   - The first click landed in the dirty Map → no visible change in the tile
//     (selectedSet re-read empty serverState).
//   - A second click on a different tile WIPED the first because toggleColor
//     re-read the empty serverState too. Same class as PR #1118 / #1120
//     (StorefrontListingPanel Category).

const catalogColors = computed(() => store.supplierMeta?.colors ?? [])

// Stranded-color ghost tiles (GH #2153 Fix 2).
//
// A selected color name that is NOT in the supplier catalog -- a "phantom
// color" (cp 54 / K455 carried "Black", "Blustery", "Cool Grey", "Marine",
// none of which K455 sells) -- got no tile here, because the grid is built
// from the catalog. No tile means no `toggleColor` target, so the color was
// structurally UNDESELECTABLE: the filmstrip synthesized a swatch for it
// (the `?`/eye badge the user reported) but has no deselect handler, and a
// direct DB edit was the only removal path.
//
// This is NOT the same case as the stranded-color guard in
// `coverageFilteredColors` below: that one keeps a selected color that IS in
// the catalog but Artifi cannot preview. A name absent from the catalog
// entirely never reaches that filter.
//
// So: union the catalog with any selected-but-not-in-catalog name and
// synthesize a minimal "ghost" tile for it, mirroring
// ColorFilmstrip.vue's fallback synthesis. `toggleColor` already removes by
// string, so the ghost is immediately deselectable -- and once removed it
// disappears for good (self-healing; the row needs no migration).
//
// Matching folds case/whitespace/the SanMar trailing-`*` version marker
// (normalizeColorNameForMatch, shared with SupplierSkuPanel). Without the
// fold a stored `"Coyote Brown "` or `"Neon Blue*"` would draw a DUPLICATE
// ghost beside its real catalog tile.
//
// Loading guard: while `supplierMetaReady` is false the catalog is empty, so
// EVERY selected color would look stranded and the grid would flash a
// screenful of ghosts that vanish when the fetch lands. Synthesize nothing
// until the real catalog is here (same rationale as ColorFilmstrip's
// loading-race guard). (The ghosts are built in strandedColors, below.)

// GH #3316. True when the catalog has RESOLVED (so the spinner is gone)
// but there is no SKU to have loaded a catalog for. Deliberately keyed on
// the EFFECTIVE attribute, the same source the shell's fetch gate reads,
// so an unsaved SKU the user just typed counts immediately.
//
// Gated on supplierMetaReady so it cannot pre-empt the loading state: a
// product WITH a sku whose fetch is still in flight shows the spinner,
// not this.
const hasNoSku = computed(
    () =>
        store.supplierMetaReady &&
        !store.getEffectiveAttribute('product_item_number'),
)

// The editor picks no default color for a style Artifi can render no color
// of (see SupplierSkuPanel), so an empty selection there needs an explanation.
const showPickPrompt = computed(
    () =>
        !!artifiStyleUnsupported?.value &&
        !artifiNotFound?.value &&
        store.supplierMetaReady &&
        !hasNoSku.value &&
        colors.value.length > 0 &&
        selectedSet.value.size === 0,
)

const strandedColors = computed(() => {
    if (!store.supplierMetaReady) return []
    const catalogKeys = new Set(
        catalogColors.value.map((c) =>
            normalizeColorNameForMatch(c.name ?? c.color_sku),
        ),
    )
    const seen = new Set()
    const out = []
    for (const sku of store.getEffectiveAttribute('selected_color_skus')
        ?? []) {
        const key = normalizeColorNameForMatch(sku)
        if (!key || catalogKeys.has(key) || seen.has(key)) continue
        seen.add(key)
        // A ghost carries no hex/swatch on purpose: we have no color data
        // for a name the catalog does not know, and the `.is-placeholder`
        // checkerboard is the honest signal for that (same reasoning as
        // colorChipStyle's "do NOT emit a grey fallback" note).
        out.push({
            color_sku: sku,
            name: sku,
            hex: null,
            swatch_url: null,
            is_stranded: true,
        })
    }
    return out
})

const colors = computed(() => [
    ...catalogColors.value,
    ...strandedColors.value,
])

// Change #3: drop colors Artifi definitively cannot decorate before the
// picker builds its tiles/counts, so an unsupported color is never a
// selectable option. STRICT fail-open (Jake's call): hide ONLY when the
// coverage fetch succeeded (not loading, no error) AND the supported set is
// non-empty AND the color's normalized name is absent. Loading / error /
// empty-set -> show every color. isArtifiUnsupported already encodes the
// loading/error/empty fail-open, but we ALSO gate on the same conditions
// here so a transient/uncertain coverage state can never silently drop a
// real sellable color. The whole picker pipeline (pills, counts, grid)
// reads from this so tiles and counts stay consistent.
const coverageFilteredColors = computed(() => {
    const all = colors.value
    // SA toggle OFF -> never HIDE; unpreviewable colors stay in the list and
    // are dimmed/flagged via isColorDimmed() in the template. STRICT fail-open
    // still applies to the DIM treatment itself (isArtifiUnsupported returns
    // false while loading/errored/empty), so nothing is dimmed on an uncertain
    // coverage state either.
    if (!hideUnpreviewable.value) return all
    // TWO SOURCES, ON PURPOSE (#2060). This gate and the per-color decision
    // below read DIFFERENT fields of the same coverage response, and that
    // divergence is deliberate -- do not "simplify" it to one field:
    //
    //   coverageReady (here)  <- supportedSet, the RAW 'supported' color list.
    //                            Answers "did a coverage fetch land at all?"
    //   isArtifiUnsupported() <- supportedNormSet, built from the KEYS of the
    //                            RESOLVED 'supported_with_variant_sku' map.
    //                            Answers "can Artifi preview THIS color?"
    //
    // Why not one field: per #2057, the DIM/HIDE decision must key off the
    // resolved variant map, because the backend keeps residual Class B colors
    // (in ColorAttribute, but with no resolvable Artifi variant SKU) in
    // 'supported' while omitting them from the map. Keying the per-color
    // decision off the raw list would leave those colors wrongly un-hidden.
    // (supportedNormSet is internal to useArtifiCoverage -- it is not on the
    // composable's public surface, so it is reachable here only through
    // isArtifiUnsupported.)
    //
    // The case that looks wrong and is not: a Class B style with a non-empty
    // 'supported' but a map that fully failed to rebuild ({}). coverageReady
    // is TRUE (the raw list is non-empty) yet isArtifiUnsupported fail-opens
    // to FALSE for every color (useArtifiCoverage.js -- an empty norm set
    // never dims). Net effect: nothing is hidden. That is the CORRECT
    // outcome -- we never hide a color we cannot prove is unpreviewable --
    // arrived at by two fail-opens rather than one.
    const coverageReady =
        !artifiLoading.value
        && !artifiError.value
        && artifiSupportedSet.value
        && artifiSupportedSet.value.size > 0
    if (!coverageReady) return all
    // Stranded-color guard (#2004 bar-raiser, Jake 2026-07-09): NEVER hide a
    // color that is already in selected_color_skus, even if Artifi now
    // reports it unsupported. Hiding a previously-chosen color would drop it
    // from the grid AND the 'selected' pill (which filters from this base),
    // so the distributor could not see or deselect it -- yet it stays
    // persisted and still renders on the storefront. Hide only when
    // unsupported AND not currently selected; an already-chosen color always
    // stays visible + deselectable.
    //
    // NO-VANISH rule (Jake 2026-10-27, PC380): nothing the user does may make
    // a swatch disappear with no way back. Two more exceptions to the hide:
    //   - a color the user DESELECTED in this panel stays (dimmed) until they
    //     leave it (releasedSkus) -- deselecting is undoable;
    //   - the "N colors can't be previewed yet" disclosure under the grid
    //     reveals every hidden color (revealHidden), so a hidden color is
    //     always one click away.
    const selected = selectedSet.value
    const released = releasedSkus.value
    const reveal = revealHidden.value
    return all.filter(
        (c) =>
            reveal
            || selected.has(c.color_sku)
            || released.has(c.color_sku)
            || !isArtifiUnsupported(c.name ?? c.color_sku),
    )
})

// The colors the hide feature is withholding (or would, if not for a
// selection / release / reveal): drives the disclosure. Empty whenever the
// hide is not in force (toggle OFF dims instead; coverage unknown fails
// open), so the disclosure only ever describes a real hide.
const unpreviewableColors = computed(() => {
    if (!hideUnpreviewable.value) return []
    const coverageReady
        = !artifiLoading.value
        && !artifiError.value
        && artifiSupportedSet.value
        && artifiSupportedSet.value.size > 0
    if (!coverageReady) return []
    return colors.value.filter((c) =>
        isArtifiUnsupported(c.name ?? c.color_sku),
    )
})

// How many of them are hidden RIGHT NOW (not selected, not just released).
const hiddenNowCount = computed(
    () =>
        unpreviewableColors.value.filter(
            (c) =>
                !selectedSet.value.has(c.color_sku)
                && !releasedSkus.value.has(c.color_sku),
        ).length,
)

const showHiddenDisclosure = computed(
    () => revealHidden.value || hiddenNowCount.value > 0,
)

const hiddenDisclosureText = computed(() => {
    if (revealHidden.value) {
        const n = unpreviewableColors.value.length
        return `Showing ${n} ${n === 1 ? 'color' : 'colors'} that can't be previewed yet. You can still select ${n === 1 ? 'it' : 'them'}, but the editor won't draw ${n === 1 ? 'it' : 'them'}.`
    }
    const n = hiddenNowCount.value
    return `${n} ${n === 1 ? "color can't" : "colors can't"} be previewed yet`
})

// Dim/flag predicate for a tile (SA toggle OFF path). A color is dimmed when
// Artifi cannot preview it -- isArtifiUnsupported already encodes STRICT
// fail-open (false while loading/errored/empty), so a color is dimmed only
// when coverage proves it unsupported. Independent of the SA toggle: when the
// toggle is ON the unsupported color is HIDDEN (never reaches the grid), so
// this predicate only visibly fires on the OFF path -- but keeping it
// toggle-agnostic keeps the treatment honest for any color that survives the
// filter (e.g. a stranded-selected color).
function isColorDimmed(color) {
    return isArtifiUnsupported(color.name ?? color.color_sku)
}

const selectedSet = computed(
    () =>
        new Set(store.getEffectiveAttribute('selected_color_skus') ?? []),
)

// Footnote list (blueprint 5.2, Phase 5): one entry per SELECTED color
// with a non-null coverageFootnote. Scoped to selected colors only --
// deselected colors are hidden or shown plainly in the grid, and "no
// preview" is only actionable (Remove) once a color is actually chosen.
// Text format is fixed by the blueprint's determination rule so this is
// the ONE place the wording is built -- ColorFilmstrip/Stage reuse
// coverageFootnote directly but do not duplicate this text composition
// for the grid's footnote list.
function footnoteText(name, footnote) {
    if (footnote.allFail) {
        return `${name} -- no preview available for this color`
    }
    return `${name} -- no preview on ${footnote.failingViews.join(', ')}`
}

const footnoteEntries = computed(() => {
    const entries = []
    for (const color of colors.value) {
        if (!selectedSet.value.has(color.color_sku)) continue
        const footnote = coverageFootnote(color.name ?? color.color_sku)
        if (!footnote) continue
        entries.push({
            color,
            footnote,
            text: footnoteText(color.name ?? color.color_sku, footnote),
        })
    }
    return entries
})

const manualLd = computed(
    () => store.getEffectiveAttribute('manual_ld') ?? {},
)

function hasManualOverride(sku) {
    return Object.prototype.hasOwnProperty.call(manualLd.value, sku)
}

// Effective light/dark for a tile: manual override wins, else auto-classify.
// Promoted to @/utils/luminance after the third inline duplicate appeared
// (bar-raiser P1 #4 on pebble 14). Thin wrapper keeps existing call sites
// using `effectiveLD(sku, hex)` rather than re-threading the manualLd map.
function effectiveLD(sku, hex) {
    return effectiveLDUtil(manualLd.value, sku, hex)
}

// ---- L/D pip rendering helpers (task #163, refined task #167) ----
//
// The pip is rendered for every tile (no v-if hide). The classification
// priority order (highest wins) is:
//   1. Manual override — distributor explicitly set L or D.
//   2. Hex-derived luminance — WCAG classifyLD on the supplier hex.
//   3. Name heuristic (high confidence) — saturated word match
//      ("Black", "White", "Carolina") OR explicit "Dark X" / "Light X"
//      modifier.
//   4. Name heuristic (low confidence) — mid-tone words (Heather,
//      Grey, Steel). Defaults to 'dark' (industry convention) but the
//      UI flags it as a guess.
//   5. Ambiguous — render '?' and let the user pick manually.
//
// ``ldPipConfidence`` returns the tier name so the template can:
//   - Style heuristic results differently from hex-derived (a small
//     visual marker indicates "we used the name").
//   - Phrase the aria-label so screen-reader users hear when we're
//     guessing.
//   - Exclude heuristic-low from the filter-pill counts (Black is
//     essentially certain; Heather is a guess — counting Heather as
//     definitive feels wrong).

function ldFromHeuristic(color) {
    return classifyByName(color?.name)
}

function ldPipState(color) {
    // Manual override wins, then hex-derived, then name heuristic
    // (regardless of confidence — confidence is reported separately).
    const ld = effectiveLD(color.color_sku, color.hex)
    if (ld === 'light' || ld === 'dark') return ld
    const heuristic = ldFromHeuristic(color)
    if (heuristic.ld === 'light' || heuristic.ld === 'dark') return heuristic.ld
    return 'ambiguous'
}

function ldPipConfidence(color) {
    if (hasManualOverride(color.color_sku)) return 'manual'
    const auto = effectiveLD(color.color_sku, color.hex)
    if (auto === 'light' || auto === 'dark') return 'auto'
    const heuristic = ldFromHeuristic(color)
    if (heuristic.confidence === 'high') return 'heuristic-high'
    if (heuristic.confidence === 'low') return 'heuristic-low'
    return 'ambiguous'
}

// Background style for a color tile's chip. Layered preference:
//   1. If swatch_url is present, render it as a cover background-image so the
//      actual photographed fabric texture shows through.
//   2. If hex is present, ALSO emit it as background-color. The hex paints
//      the chip first; the swatch image (if it loads) covers it. If the
//      swatch image fails to load — or, as observed 2026-06-17, SanMar's CDN
//      starts returning HTTP 200 with an HTML "File Not Found" body that the
//      browser silently fails to render — the hex shows through as a
//      graceful fallback instead of the tile's white background.
//   3. Neither: return {} and let the ``.is-placeholder`` CSS class paint a
//      checkerboard. Do NOT emit a grey fallback color here — the
//      checkerboard pattern is the honest signal that we have no color data.
function colorChipStyle(color) {
    const hasSwatch = Boolean(color?.swatch_url)
    const hasHex = Boolean(color?.hex)
    if (hasSwatch && hasHex) {
        return {
            backgroundColor: color.hex,
            backgroundImage: `url(${color.swatch_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        }
    }
    if (hasSwatch) {
        return {
            backgroundImage: `url(${color.swatch_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        }
    }
    if (hasHex) {
        return { backgroundColor: color.hex }
    }
    return {}
}

// ---- filter pipeline ----

// Color counts as "definitively" classified for filter / count purposes
// when its confidence is manual, auto (hex-derived), or heuristic-high
// (saturated dictionary word — essentially certain). Heuristic-low is a
// guess and ambiguous is no signal at all; both excluded so the user
// isn't misled by overconfident bulk counts.
function classifiedLD(color) {
    const conf = ldPipConfidence(color)
    if (conf === 'manual' || conf === 'auto' || conf === 'heuristic-high') {
        return ldPipState(color)
    }
    return null
}

const filteredByPill = computed(() => {
    const base = coverageFilteredColors.value
    if (activeFilter.value === 'all') return base
    if (activeFilter.value === 'selected') {
        return base.filter((c) => selectedSet.value.has(c.color_sku))
    }
    return base.filter((c) => classifiedLD(c) === activeFilter.value)
})

const visibleColors = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return filteredByPill.value
    // bar-raiser P2-2: tile labels use ``color.name || color.color_sku``, so
    // searching by the visible name must match. Filter on either field.
    return filteredByPill.value.filter(
        (c) =>
            (c.color_sku || '').toLowerCase().includes(q)
            || (c.name || '').toLowerCase().includes(q),
    )
})

// ---- counts ----

const filterCounts = computed(() => {
    // Counts reflect only pickable (coverage-filtered) colors so the pill
    // totals match the tiles actually rendered.
    const base = coverageFilteredColors.value
    const all = base.length
    let light = 0
    let dark = 0
    for (const c of base) {
        const ld = classifiedLD(c)
        if (ld === 'light') light += 1
        else if (ld === 'dark') dark += 1
    }
    return {
        all,
        light,
        dark,
        selected: selectedSet.value.size,
    }
})

const statusFooterCounts = computed(() => {
    let light = 0
    let dark = 0
    for (const c of colors.value) {
        if (!selectedSet.value.has(c.color_sku)) continue
        const ld = classifiedLD(c)
        if (ld === 'light') light += 1
        else if (ld === 'dark') dark += 1
    }
    return {
        selected: selectedSet.value.size,
        light,
        dark,
        // Decoration routing lands in pebble 7+. Status row shows 0 until
        // that data exists.
        decorations: 0,
    }
})

// ---- actions ----

// Unrenderable-colors Phase 2 (blueprint section 3): resolve one or more
// color-name strings into the 3-field {color_name, mainframe_code,
// artifi_variant_sku} shape via the backend's pure-resolver endpoint. This
// is client-initiated, server-executed -- resolving artifi_variant_sku
// needs the Artifi client + a catalog fetch, which does not belong in the
// browser. The endpoint does NOT write the DB; the resolved objects are
// folded into the SAME staged store.updateField('selected_colors', ...)
// the click already performs for selected_color_skus (PATCH-on-Save owns
// the write -- resolution only enriches what gets staged, "one write
// model", design-philosophy.md).
//
// Fail-open on network/HTTP failure (mirrors useArtifiCoverage's fail-open
// contract): a resolver outage must not block color selection. On error,
// every requested name resolves to {mainframe_code: null,
// artifi_variant_sku: null} -- the color is still selectable, it just
// hasn't been enriched with Artifi identity yet.
async function resolveSelectedColors(names) {
    const cpId = store.campaignProductId
    if (!cpId || names.length === 0) {
        return names.map((name) => ({
            color_name: name,
            mainframe_code: null,
            artifi_variant_sku: null,
        }))
    }
    try {
        const resp = await axiosInstance.post(
            `/api/campaign_products/${cpId}/resolve_selected_colors`,
            { color_names: names },
        )
        return resp.data?.selected_colors ?? []
    } catch (err) {
        console.warn(
            '[ColorsPanel] resolve_selected_colors failed, falling back to unresolved identity:',
            err?.message ?? err,
        )
        return names.map((name) => ({
            color_name: name,
            mainframe_code: null,
            artifi_variant_sku: null,
        }))
    }
}

// Jake 2026-10-06 (card https://trello.com/c/Qlwx8mlT): deselecting a color
// removes it from the storefront listing -- its decorated render AND its
// blank images, i.e. the whole storefront_images[name] key. This REVERSES
// the earlier "de-selecting never prunes" rule (card q7EQ3a0b); the Chosen
// grid still badges a non-selected color's images, which now only happens
// for data saved before this rule. Deleting the KEY rather than emptying it
// means a re-selected color starts fresh (its render is default-included
// again -- see includeColorRender). storefront_images is keyed by color
// NAME; resolve each sku the same way the listing panel does.
function dropColorsFromStorefrontListing(skus) {
    if (!skus.length) return
    const names = colorNamesForSkus(skus, store.supplierMeta?.colors)
    const current = store.getEffectiveAttribute('storefront_images') ?? {}
    const next = withoutColors(current, names)
    if (next === current) return
    // Remember exactly what was removed so Undo can put it back.
    const removed = {}
    for (const name of names) {
        if (Object.prototype.hasOwnProperty.call(current, name)) {
            removed[name] = current[name]
        }
    }
    store.updateField('storefront_images', next)
    offerPruneUndo(removed)
}

const PRUNE_UNDO_TIMEOUT_MS = 10_000
const pruneUndo = ref({ open: false, message: '', removed: null })

function offerPruneUndo(removed) {
    const colors = Object.keys(removed)
    const message = colors.length === 1
        ? `Removed ${colors[0]} from the storefront listing.`
        : `Removed ${colors.length} colors from the storefront listing.`
    pruneUndo.value = { open: true, message, removed }
}

// Put the removed storefront_images entries back exactly as they were, each
// image in the global display slot it held (the prune closed the gap; see
// restoreColors). The color's SELECTION is not restored: re-selecting it is
// the distributor's call, and a restored entry keeps its curation (pool-only
// render rule).
function undoPrune() {
    const removed = pruneUndo.value.removed
    pruneUndo.value = { open: false, message: '', removed: null }
    if (!removed) return
    const current = store.getEffectiveAttribute('storefront_images') ?? {}
    const selectedNames = colorNamesForSkus(
        store.getEffectiveAttribute('selected_color_skus') ?? [],
        store.supplierMeta?.colors,
    )
    store.updateField('storefront_images', restoreColors(current, removed, selectedNames))
}

// Day 2 (canonical product editor): ALSO move the persistent Stage to the
// clicked color. The intent is 'tick': the Stage itself makes no Artifi call,
// but the render manager (useStorefrontColorRenders) renders a ticked color
// at once (Jake 2026-10-28) and nothing for an untick. The legacy editor
// doesn't subscribe to productEditorUiState; this write is a no-op for it.
// The narrow
// try/catch wraps ONLY the Pinia resolution (the documented "no active
// Pinia in some test contexts" path); setSelectedColor itself is allowed to
// surface real errors.
function moveStageToTickedColor(sku) {
    let editorUi
    try {
        editorUi = useProductEditorUiState()
    } catch (err) {
        // No active Pinia (legacy editor in some test paths). Skip the
        // ui-state write; the primary selection state is already updated.
        console.warn(
            '[ColorsPanel] useProductEditorUiState unavailable, skipping Stage sync:',
            err?.message ?? err,
        )
        return
    }
    editorUi.setSelectedColor(sku, 'tick')
}

async function toggleColor(sku) {
    // Read through getEffectiveAttribute so consecutive clicks compose
    // against the latest dirty state, not the stale serverState. See header
    // comment on selectedSet/manualLd for the bug context.
    const current
        = store.getEffectiveAttribute('selected_color_skus') ?? []
    let next
    const isDeselecting = current.includes(sku)
    if (isDeselecting) {
        next = current.filter((s) => s !== sku)
    } else {
        next = [...current, sku]
    }
    store.updateField('selected_color_skus', next)
    if (isDeselecting) {
        // Keep it on the grid (dimmed) so the deselect is undoable even when
        // the hide feature would otherwise withhold this color.
        releasedSkus.value = new Set([...releasedSkus.value, sku])
        dropColorsFromStorefrontListing([sku])
    }
    // BEFORE the resolve round trip below (#4146 gate P2-4): on a product
    // with no colors the Stage would otherwise auto-pick this first color
    // during the await with intent 'open', which renders.
    moveStageToTickedColor(sku)

    // selected_colors: for a NEW selection, the resolve call is async
    // (a network round trip) -- re-read getEffectiveAttribute('selected_colors')
    // AFTER the await, not before, so a second rapid click landing while the
    // first is still resolving composes against the first click's write
    // instead of clobbering it (same stale-read class as the 2026-05-18
    // "can't select colors" bug this file's header documents -- the fix
    // there was reading through getEffectiveAttribute; here the added
    // wrinkle is an `await` in between the read and the write, so the read
    // must happen on the FAR side of it).
    if (isDeselecting) {
        // Deselect is a pure local filter -- D4 "soft remove": the color's
        // resolved identity is simply dropped from the selected list, no
        // network call, fully reversible by re-selecting.
        const currentColors
            = store.getEffectiveAttribute('selected_colors') ?? []
        store.updateField(
            'selected_colors',
            currentColors.filter((c) => c.color_name !== sku),
        )
    } else {
        const [resolved] = await resolveSelectedColors([sku])
        const currentColors
            = store.getEffectiveAttribute('selected_colors') ?? []
        store.updateField('selected_colors', [...currentColors, resolved])
    }
}

// setLDOverride -- called by LDPipPopover's update:manual-ld emit.
// val is 'light' | 'dark' | null (null = clear override, revert to Auto).
//
// L/D pip Option-B (per-color override): writes to TWO fields.
//
//   1. ``color_overrides`` -- the authoritative per-color override the
//      resolver short-circuits on (tier order is preempted when the
//      override matches an available catch_all variant). Migration:
//      route_fork_schema_055.
//   2. ``manual_ld`` -- still drives the L/D pip badge state, color-
//      tile chip classification, filter counts, etc. (effectiveLD,
//      classifyByName, ldPipState). Same value, same shape -- the dual
//      write keeps display unchanged while the resolver gets the true
//      override. Follow-up: collapse the display-side readers onto
//      ``color_overrides`` and drop ``manual_ld`` (P1 follow-up).
function setLDOverride(sku, val) {
    const currentOverrides
        = store.getEffectiveAttribute('color_overrides') ?? {}
    const currentManual
        = store.getEffectiveAttribute('manual_ld') ?? {}
    let nextOverrides
    let nextManual
    if (val === null) {
        // Auto: clear the key from both maps. The validator on the
        // model also strips 'auto' values, but we encode "no override"
        // as key absence for symmetry with the legacy manual_ld shape.
        nextOverrides = Object.fromEntries(
            Object.entries(currentOverrides).filter(([k]) => k !== sku),
        )
        nextManual = Object.fromEntries(
            Object.entries(currentManual).filter(([k]) => k !== sku),
        )
    } else {
        // Light or Dark: set the explicit value on both maps.
        nextOverrides = { ...currentOverrides, [sku]: val }
        nextManual = { ...currentManual, [sku]: val }
    }
    store.updateField('color_overrides', nextOverrides)
    store.updateField('manual_ld', nextManual)
}

async function selectAllVisible() {
    const current
        = store.getEffectiveAttribute('selected_color_skus') ?? []
    const currentColors
        = store.getEffectiveAttribute('selected_colors') ?? []
    const merged = new Set(current)
    for (const c of visibleColors.value) {
        merged.add(c.color_sku)
    }
    store.updateField('selected_color_skus', Array.from(merged))

    // Batch: ONE resolve_selected_colors call for every NEWLY-added name
    // (blueprint section 3, "selectAllVisible batches") rather than N
    // sequential single-color calls. Names already present in
    // selected_colors are skipped -- no redundant re-resolution.
    const alreadyResolvedNames = new Set(
        currentColors.map((c) => c.color_name),
    )
    const newlyAddedNames = visibleColors.value
        .map((c) => c.color_sku)
        .filter(
            (sku) => !current.includes(sku) && !alreadyResolvedNames.has(sku),
        )
    if (newlyAddedNames.length === 0) {
        return
    }
    const resolved = await resolveSelectedColors(newlyAddedNames)
    // Re-read AFTER the await (same rationale as toggleColor above): a
    // rapid click on another tile while this resolve call is in flight
    // must not be clobbered by writing against the pre-await snapshot.
    const latestColors = store.getEffectiveAttribute('selected_colors') ?? []
    store.updateField('selected_colors', [...latestColors, ...resolved])
}

function removeAll() {
    const wasSelected
        = store.getEffectiveAttribute('selected_color_skus') ?? []
    store.updateField('selected_color_skus', [])
    dropColorsFromStorefrontListing([...new Set(wasSelected)])
    store.updateField('selected_colors', [])
}

function scrollToDecorations() {
    const target = document.querySelector(
        '[data-testid="editor-section-4"]',
    )
    if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth' })
    }
}
</script>

<style scoped>
.colors-header {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
}

.colors-header-icon {
    flex: 0 0 auto;
    font-size: 28px;
}

.colors-header-text {
    flex: 1 1 auto;
    min-width: 0;
}

.colors-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 1.2;
}

.colors-subtitle {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 2px;
}

.colors-toolbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.colors-filter-pills {
    display: flex;
    flex-direction: row;
    gap: 6px;
}

.colors-filter-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    background: transparent;
    font-size: 12px;
    line-height: 1.2;
    cursor: pointer;
}

.colors-filter-pill.is-active {
    background-color: rgba(25, 118, 210, 0.12);
    border-color: rgba(25, 118, 210, 0.6);
    color: rgb(25, 118, 210);
}

.colors-filter-pill-count {
    font-weight: 600;
    background: rgba(0, 0, 0, 0.08);
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 11px;
}

.colors-search {
    flex: 1 1 180px;
    max-width: 240px;
}

.colors-select-all-visible {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    background: transparent;
    font-size: 12px;
    cursor: pointer;
}

.colors-select-all-icon {
    font-size: 16px;
}

.colors-remove-all {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    background: transparent;
    font-size: 12px;
    cursor: pointer;
    color: #b91c1c;
}

.colors-remove-all:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.colors-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 4px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.875rem;
}

.colors-no-sku {
    padding: 12px 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
}

.colors-pick-prompt {
    padding: 12px 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
}

.colors-swatch-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
    gap: 5px;
}

.colors-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 4px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: white;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
}

.colors-tile.is-selected {
    border-color: rgb(25, 118, 210);
    box-shadow: 0 0 0 1px rgb(25, 118, 210);
}

/*
 * SA toggle OFF: an Artifi-unpreviewable color is shown DIMMED + flagged
 * (slashed-eye) rather than hidden. The chip/name are dimmed so the color
 * still reads as "available but no preview". The tile stays clickable
 * (selectable) -- dimming is a visual flag, not a disable.
 */
.colors-tile.is-artifi-unsupported .color-chip,
.colors-tile.is-artifi-unsupported .color-name,
.colors-tile.is-artifi-unsupported .color-sku-code {
    opacity: 0.45;
}

.colors-tile-unsupported-eye {
    position: absolute;
    top: 2px;
    right: 2px;
    font-size: 14px;
    line-height: 1;
    color: rgba(0, 0, 0, 0.6);
    background: rgba(255, 255, 255, 0.85);
    border-radius: 50%;
    padding: 1px;
}

.color-chip {
    height: 28px;
    border-radius: 2px;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.color-chip.is-placeholder {
    background-image: linear-gradient(
            45deg,
            #c4c4c4 25%,
            transparent 25%
        ),
        linear-gradient(-45deg, #c4c4c4 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #c4c4c4 75%),
        linear-gradient(-45deg, transparent 75%, #c4c4c4 75%);
    background-size: 8px 8px;
    background-position: 0 0, 0 4px, 4px -4px, -4px 0;
}

.color-name {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.color-sku-code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px;
    color: rgba(0, 0, 0, 0.55);
}

/*
 * Stranded ghost tile (GH #2153 Fix 2). A dashed border + muted note marks
 * a tile that exists ONLY so a phantom color -- one not in the product's
 * current catalog -- can be removed. Deliberately distinct from the
 * Artifi-unsupported dimming above: that color is real and selectable, this
 * one is not real at all, and the only useful action is to remove it.
 */
.colors-tile.is-stranded {
    border-style: dashed;
    border-color: rgba(0, 0, 0, 0.28);
    background: rgba(0, 0, 0, 0.02);
}

.colors-tile-stranded-note {
    font-size: 9px;
    line-height: 1.2;
    color: rgba(0, 0, 0, 0.55);
    font-style: italic;
}

.check-pip {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgb(25, 118, 210);
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
}

.ld-pip {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 16px;
    height: 16px;
    padding: 0 3px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(0, 0, 0, 0.25);
    color: rgba(0, 0, 0, 0.75);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    line-height: 1;
}

.ld-pip.is-manual {
    border-style: dashed;
}

/* Ambiguous state (no hex, no manual override): muted, "?" inside. Quiet
   indicator that's still clearly clickable — restores the entry point for
   colors where the supplier ships a swatch_url but no hex. Task #163. */
.ld-pip.is-ambiguous {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(0, 0, 0, 0.15);
    color: rgba(0, 0, 0, 0.5);
}

/* Name-heuristic confidence indicators (task #167). Hex-derived results
   stay on the default style (no marker — we're confident). Heuristic
   guesses carry a thin dotted underline below the L/D letter so the user
   can tell at a glance that we used the color NAME, not the hex. Low-
   confidence (Heather / Grey / mid-tones) layers reduced opacity on top
   of the same dotted underline. */
.ld-pip.is-heuristic-high {
    text-decoration: underline dotted rgba(0, 0, 0, 0.3);
    text-underline-offset: 1px;
}

.ld-pip.is-heuristic-low {
    text-decoration: underline dotted rgba(0, 0, 0, 0.3);
    text-underline-offset: 1px;
    opacity: 0.7;
}

.colors-status-footer {
    margin-top: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
}

.colors-hidden-disclosure {
    margin-top: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
}

.colors-hidden-toggle {
    color: rgb(25, 118, 210);
    font-weight: 500;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.colors-decoration-link {
    color: rgb(25, 118, 210);
    text-decoration: none;
    font-weight: 500;
    margin-left: auto;
}

/* Unrenderable-colors footnote dot (blueprint 5.2, Phase 5): a small,
   distinct badge -- separate from the check pip (top-left) and L/D pip
   (top-right) -- flagging a preview gap on a selected color. Bottom-left
   placement avoids overlapping either existing pip. Detail lives in the
   footnote list below the grid, not in this dot's own affordance. */
.colors-tile-footnote-dot {
    position: absolute;
    bottom: 2px;
    left: 2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #f59e0b;
    border: 1px solid rgba(0, 0, 0, 0.25);
}

.colors-footnote-list {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 12px;
}

.colors-footnote-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.75);
}

.colors-footnote-text {
    flex: 1 1 auto;
    min-width: 0;
}

.colors-footnote-remove {
    flex: 0 0 auto;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: transparent;
    border-radius: 4px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.7);
    cursor: pointer;
}
</style>
